<template>
  <div class="space-y-6">
    <div class="flex items-center gap-3">
      <button @click="$router.back()" class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
        <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
        Volver a Reportes
      </button>
    </div>

    <div v-if="loading" class="flex items-center justify-center py-24">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
    </div>

    <template v-else-if="reportData">
      <!-- User Report Detail -->
      <template v-if="type === 'user'">
        <div class="bg-gradient-to-r from-amber-500 to-red-600 rounded-xl p-6 text-white">
          <div class="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 class="text-2xl font-bold">Detalle del Reporte</h1>
              <p class="text-amber-100 mt-1">{{ formatDate(reportData.report?.createdAt) }}</p>
            </div>
            <div class="flex gap-2 flex-wrap">
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20">
                Categoría: {{ reasonLabel(reportData.report?.reason) }}
              </span>
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" :class="statusClass(reportData.report?.status)">{{ statusLabel(reportData.report?.status) }}</span>
            </div>
          </div>
        </div>

        <div v-if="reportData.report?.details" class="card p-5">
          <p class="text-sm font-semibold text-amber-700 mb-1">Descripción del reporte</p>
          <p class="text-gray-800">{{ reportData.report.details }}</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Reportante -->
          <div class="card p-6 border-l-4 border-blue-500">
            <div class="flex items-center justify-between mb-4">
              <p class="text-xs font-semibold text-blue-600 uppercase tracking-wide">Reportante</p>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="!reportData.report?.reporter?.isBlocked ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                {{ !reportData.report?.reporter?.isBlocked ? 'Activo' : 'Bloqueado' }}
              </span>
            </div>
            <div class="flex items-center mb-4">
              <div v-if="reportData.report?.reporter?.avatar" class="h-14 w-14 rounded-full overflow-hidden mr-4 flex-shrink-0 ring-2 ring-blue-200">
                <img :src="reportData.report.reporter.avatar" class="h-full w-full object-cover" />
              </div>
              <div v-else class="h-14 w-14 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                {{ reportData.report?.reporter?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="font-semibold text-gray-900 text-lg">{{ reportData.report?.reporter?.name }}</p>
                <p class="text-sm text-gray-500">{{ reportData.report?.reporter?.email }}</p>
              </div>
            </div>
            <p class="text-sm text-gray-500 mb-3">Registrado: {{ reportData.report?.reporter?.createdAt ? formatDate(reportData.report.reporter.createdAt) : '-' }}</p>
            <div v-if="reportData.report?.reporter?.pets?.length" class="mb-3">
              <p class="text-xs font-semibold text-gray-600 mb-2">Mascotas</p>
              <div class="flex flex-wrap gap-1.5">
                <span v-for="pet in reportData.report.reporter.pets" :key="pet.id" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {{ pet.name }} · {{ pet.breed?.animalType?.name || pet.breed?.name }}
                </span>
              </div>
            </div>
            <div v-if="reportData.report?.reporter?.isBlocked && reportData.report?.reporter?.blockedReason" class="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <span class="font-medium">Motivo del bloqueo:</span> {{ reportData.report.reporter.blockedReason }}
              <span v-if="reportData.report.reporter.blockedUntil" class="block mt-0.5 text-xs text-red-500">Hasta: {{ formatDate(reportData.report.reporter.blockedUntil) }}</span>
            </div>
          </div>

          <!-- Reportado -->
          <div class="card p-6 border-l-4 border-red-500">
            <div class="flex items-center justify-between mb-4">
              <p class="text-xs font-semibold text-red-600 uppercase tracking-wide">Reportado</p>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="!reportData.report?.reported?.isBlocked ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                {{ !reportData.report?.reported?.isBlocked ? 'Activo' : 'Bloqueado' }}
              </span>
            </div>
            <div class="flex items-center mb-4">
              <div v-if="reportData.report?.reported?.avatar" class="h-14 w-14 rounded-full overflow-hidden mr-4 flex-shrink-0 ring-2 ring-red-200">
                <img :src="reportData.report.reported.avatar" class="h-full w-full object-cover" />
              </div>
              <div v-else class="h-14 w-14 rounded-full bg-gradient-to-r from-red-500 to-orange-600 flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                {{ reportData.report?.reported?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="font-semibold text-gray-900 text-lg">{{ reportData.report?.reported?.name }}</p>
                <p class="text-sm text-gray-500">{{ reportData.report?.reported?.email }}</p>
              </div>
            </div>
            <p class="text-sm text-gray-500 mb-3">Registrado: {{ reportData.report?.reported?.createdAt ? formatDate(reportData.report.reported.createdAt) : '-' }}</p>
            <div v-if="reportData.report?.reported?.pets?.length" class="mb-3">
              <p class="text-xs font-semibold text-gray-600 mb-2">Mascotas</p>
              <div class="flex flex-wrap gap-1.5">
                <span v-for="pet in reportData.report.reported.pets" :key="pet.id" class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  {{ pet.name }} · {{ pet.breed?.animalType?.name || pet.breed?.name }}
                </span>
              </div>
            </div>
            <div v-if="reportData.report?.reported?.isBlocked && reportData.report?.reported?.blockedReason" class="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <span class="font-medium">Motivo del bloqueo:</span> {{ reportData.report.reported.blockedReason }}
              <span v-if="reportData.report.reported.blockedUntil" class="block mt-0.5 text-xs text-red-500">Hasta: {{ formatDate(reportData.report.reported.blockedUntil) }}</span>
            </div>
          </div>
        </div>

        <!-- Historial de chat -->
        <div class="card overflow-hidden">
          <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div class="flex items-center">
              <svg class="h-5 w-5 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
              <span class="font-semibold text-gray-800">Historial de Chat</span>
            </div>
            <span class="text-sm text-gray-500">{{ reportData.chatMessages?.length || 0 }} mensajes</span>
          </div>
          <div v-if="!reportData.chatMessages?.length" class="py-14 text-center text-sm text-gray-400">
            No hay historial de chat entre estos usuarios
          </div>
          <div v-else class="h-[500px] overflow-y-auto p-6 space-y-4 bg-gray-50">
            <div
              v-for="msg in reportData.chatMessages"
              :key="msg.id"
              class="flex"
              :class="msg.sender?.id === reportData.report.reporterId ? 'justify-start' : 'justify-end'"
            >
              <div class="max-w-sm lg:max-w-lg">
                <div class="flex items-end gap-2" :class="msg.sender?.id === reportData.report.reporterId ? 'flex-row' : 'flex-row-reverse'">
                  <div class="h-7 w-7 rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold shadow" :class="msg.sender?.id === reportData.report.reporterId ? 'bg-blue-500' : 'bg-red-500'">
                    {{ msg.sender?.name?.charAt(0).toUpperCase() }}
                  </div>
                  <div class="rounded-2xl px-4 py-2.5 shadow-sm" :class="msg.sender?.id === reportData.report.reporterId ? 'bg-white border border-gray-200 rounded-tl-none' : 'bg-amber-500 text-white rounded-tr-none'">
                    <img v-if="msg.messageType === 'image' && msg.imageUrl" :src="msg.imageUrl" class="max-w-[220px] rounded-lg" />
                    <p v-else class="text-sm leading-relaxed">{{ msg.content }}</p>
                    <p class="text-xs mt-1.5 opacity-60">{{ new Date(msg.createdAt).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Acciones del reporte -->
        <div class="card p-6 flex items-center justify-end gap-3">
          <button v-if="!['resolved', 'dismissed'].includes(reportData.report?.status)" @click="openDecisionModal" :disabled="actionLoading" class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors">
            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Tomar decisión
          </button>
          <button @click="confirmDelete" class="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            Eliminar reporte
          </button>
        </div>
      </template>

      <!-- Message Report Detail -->
      <template v-else-if="type === 'message'">
        <div class="bg-gradient-to-r from-amber-500 to-red-600 rounded-xl p-6 text-white">
          <div class="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h1 class="text-2xl font-bold">Detalle del Reporte de Mensaje</h1>
              <p class="text-amber-100 mt-1">{{ formatDate(reportData.report?.createdAt) }}</p>
            </div>
            <div class="flex gap-2 flex-wrap">
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white/20">
                Categoría: {{ reasonLabel(reportData.report?.reason) }}
              </span>
              <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" :class="statusClass(reportData.report?.status)">{{ statusLabel(reportData.report?.status) }}</span>
            </div>
          </div>
        </div>

        <div v-if="reportData.report?.details" class="card p-5">
          <p class="text-sm font-semibold text-amber-700 mb-1">Descripción del reporte</p>
          <p class="text-gray-800">{{ reportData.report.details }}</p>
        </div>

        <!-- Mensaje reportado -->
        <div class="card p-6 border-l-4 border-red-500">
          <div class="flex items-center justify-between mb-4">
            <p class="text-sm font-semibold text-red-600 uppercase tracking-wide">Mensaje Reportado</p>
            <span class="text-sm text-gray-400">{{ reportData.report?.message?.createdAt ? new Date(reportData.report.message.createdAt).toLocaleString('es-ES') : '' }}</span>
          </div>
          <div class="flex items-center mb-3 gap-3">
            <div v-if="reportData.report?.message?.sender?.avatar" class="h-9 w-9 rounded-full overflow-hidden flex-shrink-0">
              <img :src="reportData.report.message.sender.avatar" class="h-full w-full object-cover" />
            </div>
            <div v-else class="h-9 w-9 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-bold flex-shrink-0">
              {{ reportData.report?.message?.sender?.name?.charAt(0).toUpperCase() }}
            </div>
            <div>
              <p class="font-medium text-gray-900">{{ reportData.report?.message?.sender?.name }}</p>
              <p class="text-xs text-gray-500">{{ reportData.report?.message?.sender?.email }}</p>
            </div>
          </div>
          <div v-if="reportData.report?.message?.messageType === 'image'">
            <img :src="reportData.report.message.imageUrl" class="max-w-xs rounded-xl border border-red-200" />
          </div>
          <blockquote v-else class="text-base text-gray-800 bg-red-50 rounded-xl p-4 border border-red-100 italic">
            "{{ reportData.report?.message?.content }}"
          </blockquote>
        </div>

        <!-- Usuarios involucrados -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Reportante -->
          <div class="card p-6 border-l-4 border-blue-500">
            <div class="flex items-center justify-between mb-4">
              <p class="text-xs font-semibold text-blue-600 uppercase tracking-wide">Reportante</p>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="!reportData.report?.reporter?.isBlocked ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                {{ !reportData.report?.reporter?.isBlocked ? 'Activo' : 'Bloqueado' }}
              </span>
            </div>
            <div class="flex items-center mb-4">
              <div v-if="reportData.report?.reporter?.avatar" class="h-14 w-14 rounded-full overflow-hidden mr-4 flex-shrink-0 ring-2 ring-blue-200">
                <img :src="reportData.report.reporter.avatar" class="h-full w-full object-cover" />
              </div>
              <div v-else class="h-14 w-14 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                {{ reportData.report?.reporter?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="font-semibold text-gray-900 text-lg">{{ reportData.report?.reporter?.name }}</p>
                <p class="text-sm text-gray-500">{{ reportData.report?.reporter?.email }}</p>
              </div>
            </div>
            <p class="text-sm text-gray-500">Registrado: {{ reportData.report?.reporter?.createdAt ? formatDate(reportData.report.reporter.createdAt) : '-' }}</p>
            <div v-if="reportData.report?.reporter?.isBlocked && reportData.report?.reporter?.blockedReason" class="mt-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <span class="font-medium">Motivo del bloqueo:</span> {{ reportData.report.reporter.blockedReason }}
              <span v-if="reportData.report.reporter.blockedUntil" class="block mt-0.5 text-xs text-red-500">Hasta: {{ formatDate(reportData.report.reporter.blockedUntil) }}</span>
            </div>
          </div>

          <!-- Autor del mensaje -->
          <div class="card p-6 border-l-4 border-orange-500">
            <div class="flex items-center justify-between mb-4">
              <p class="text-xs font-semibold text-orange-600 uppercase tracking-wide">Autor del Mensaje</p>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :class="!reportData.report?.message?.sender?.isBlocked ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
                {{ !reportData.report?.message?.sender?.isBlocked ? 'Activo' : 'Bloqueado' }}
              </span>
            </div>
            <div class="flex items-center mb-4">
              <div v-if="reportData.report?.message?.sender?.avatar" class="h-14 w-14 rounded-full overflow-hidden mr-4 flex-shrink-0 ring-2 ring-orange-200">
                <img :src="reportData.report.message.sender.avatar" class="h-full w-full object-cover" />
              </div>
              <div v-else class="h-14 w-14 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-bold text-xl mr-4 flex-shrink-0">
                {{ reportData.report?.message?.sender?.name?.charAt(0).toUpperCase() }}
              </div>
              <div>
                <p class="font-semibold text-gray-900 text-lg">{{ reportData.report?.message?.sender?.name }}</p>
                <p class="text-sm text-gray-500">{{ reportData.report?.message?.sender?.email }}</p>
              </div>
            </div>
            <p class="text-sm text-gray-500">Registrado: {{ reportData.report?.message?.sender?.createdAt ? formatDate(reportData.report.message.sender.createdAt) : '-' }}</p>
            <div v-if="reportData.report?.message?.sender?.isBlocked && reportData.report?.message?.sender?.blockedReason" class="mt-3 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              <span class="font-medium">Motivo del bloqueo:</span> {{ reportData.report.message.sender.blockedReason }}
              <span v-if="reportData.report.message.sender.blockedUntil" class="block mt-0.5 text-xs text-red-500">Hasta: {{ formatDate(reportData.report.message.sender.blockedUntil) }}</span>
            </div>
          </div>
        </div>

        <!-- Acciones del reporte -->
        <div class="card p-6 flex items-center justify-end gap-3">
          <button v-if="!['resolved', 'dismissed'].includes(reportData.report?.status)" @click="openDecisionModal" :disabled="actionLoading" class="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors">
            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Tomar decisión
          </button>
          <button @click="confirmDelete" class="inline-flex items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
            <svg class="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
            Eliminar reporte
          </button>
        </div>
      </template>
    </template>

    <div v-else-if="!loading" class="card p-12 text-center text-gray-400">
      Reporte no encontrado
    </div>

    <!-- DECISION MODAL -->
    <div v-if="showDecisionModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <div class="fixed inset-0 bg-gray-900 bg-opacity-60" @click="showDecisionModal = false"></div>
        <div class="relative bg-white rounded-xl shadow-xl w-full max-w-lg z-10">
          <div class="bg-gradient-to-r from-amber-500 to-red-600 px-6 py-4 rounded-t-xl">
            <h3 class="text-lg font-bold text-white">Tomar Decisión</h3>
            <p class="text-amber-100 text-sm mt-0.5">Selecciona la acción a tomar sobre este reporte</p>
          </div>
          <div class="px-6 py-5 space-y-3">
            <div v-if="type === 'user'">
              <!-- Bloquear al reportado -->
              <div
                class="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
                :class="decisionAction === 'block_reported' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'"
                @click="decisionAction = 'block_reported'"
              >
                <div class="mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0" :class="decisionAction === 'block_reported' ? 'border-red-500' : 'border-gray-400'">
                  <div v-if="decisionAction === 'block_reported'" class="h-2 w-2 rounded-full bg-red-500"></div>
                </div>
                <div>
                  <p class="font-medium text-gray-900">Bloquear a {{ reportData.report?.reported?.name }}</p>
                  <p class="text-sm text-gray-500">El usuario reportado no podrá acceder a la app</p>
                </div>
              </div>
              <!-- Bloquear al reportante -->
              <div
                class="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
                :class="decisionAction === 'block_reporter' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'"
                @click="decisionAction = 'block_reporter'"
              >
                <div class="mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0" :class="decisionAction === 'block_reporter' ? 'border-orange-500' : 'border-gray-400'">
                  <div v-if="decisionAction === 'block_reporter'" class="h-2 w-2 rounded-full bg-orange-500"></div>
                </div>
                <div>
                  <p class="font-medium text-gray-900">Bloquear a {{ reportData.report?.reporter?.name }}</p>
                  <p class="text-sm text-gray-500">El usuario que hizo el reporte no podrá acceder a la app (reporte falso)</p>
                </div>
              </div>
            </div>
            <div v-else-if="type === 'message'">
              <!-- Bloquear al autor del mensaje -->
              <div
                class="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
                :class="decisionAction === 'block_sender' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'"
                @click="decisionAction = 'block_sender'"
              >
                <div class="mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0" :class="decisionAction === 'block_sender' ? 'border-red-500' : 'border-gray-400'">
                  <div v-if="decisionAction === 'block_sender'" class="h-2 w-2 rounded-full bg-red-500"></div>
                </div>
                <div>
                  <p class="font-medium text-gray-900">Bloquear a {{ reportData.report?.message?.sender?.name }}</p>
                  <p class="text-sm text-gray-500">El autor del mensaje no podrá acceder a la app</p>
                </div>
              </div>
              <!-- Eliminar mensaje -->
              <div
                class="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
                :class="decisionAction === 'delete_message' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'"
                @click="decisionAction = 'delete_message'"
              >
                <div class="mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0" :class="decisionAction === 'delete_message' ? 'border-orange-500' : 'border-gray-400'">
                  <div v-if="decisionAction === 'delete_message'" class="h-2 w-2 rounded-full bg-orange-500"></div>
                </div>
                <div>
                  <p class="font-medium text-gray-900">Solo eliminar el mensaje</p>
                  <p class="text-sm text-gray-500">El mensaje es eliminado y el reporte se marca como resuelto</p>
                </div>
              </div>
            </div>
            <!-- Desestimar reporte -->
            <div
              class="flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all"
              :class="decisionAction === 'dismiss' ? 'border-gray-500 bg-gray-50' : 'border-gray-200 hover:border-gray-300'"
              @click="decisionAction = 'dismiss'"
            >
              <div class="mt-0.5 h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0" :class="decisionAction === 'dismiss' ? 'border-gray-500' : 'border-gray-400'">
                <div v-if="decisionAction === 'dismiss'" class="h-2 w-2 rounded-full bg-gray-500"></div>
              </div>
              <div>
                <p class="font-medium text-gray-900">Desestimar reporte</p>
                <p class="text-sm text-gray-500">El reporte se marca como descartado sin ninguna acción adicional</p>
              </div>
            </div>
            <!-- Duración y motivo si se bloquea -->
            <div v-if="decisionAction && decisionAction !== 'dismiss' && decisionAction !== 'delete_message'" class="mt-2 space-y-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1.5">Duración del bloqueo</label>
                <div class="flex flex-wrap gap-2">
                  <button
                    v-for="opt in blockDurationOptions"
                    :key="opt.value"
                    @click="blockDuration = opt.value"
                    type="button"
                    class="px-3 py-1.5 text-sm rounded-lg border-2 transition-all"
                    :class="blockDuration === opt.value ? 'border-amber-500 bg-amber-50 text-amber-700 font-medium' : 'border-gray-200 text-gray-600 hover:border-gray-300'"
                  >
                    {{ opt.label }}
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1.5">Motivo del bloqueo (opcional)</label>
                <textarea
                  v-model="blockReasonText"
                  rows="2"
                  placeholder="Ej: Comportamiento inapropiado, acoso a otros usuarios..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2 rounded-b-xl">
            <button @click="executeDecision" :disabled="actionLoading || !decisionAction" class="btn btn-primary">{{ actionLoading ? 'Procesando...' : 'Confirmar' }}</button>
            <button @click="showDecisionModal = false" class="btn btn-secondary">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- DELETE CONFIRM MODAL -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="showDeleteModal = false"></div>
        <div class="relative bg-white rounded-lg shadow-xl w-full max-w-md z-10">
          <div class="px-6 pt-6 pb-4">
            <div class="flex items-start gap-4">
              <div class="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100">
                <svg class="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
              </div>
              <div>
                <h3 class="text-lg font-medium text-gray-900">Eliminar Reporte</h3>
                <p class="text-sm text-gray-500 mt-1">¿Estás seguro de que quieres eliminar este reporte? Esta acción no se puede deshacer.</p>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-6 py-3 flex flex-row-reverse gap-2 rounded-b-lg">
            <button @click="executeDelete" :disabled="actionLoading" class="btn btn-danger">{{ actionLoading ? 'Eliminando...' : 'Eliminar' }}</button>
            <button @click="showDeleteModal = false" class="btn btn-secondary">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toastification'
import reportService from '@/services/reportService'

export default {
  name: 'ReportDetail',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toast = useToast()

    const type = route.params.type
    const id = Number(route.params.id)

    const loading = ref(true)
    const actionLoading = ref(false)
    const reportData = ref(null)
    const showDecisionModal = ref(false)
    const decisionAction = ref(null)
    const blockReasonText = ref('')
    const blockDuration = ref('indefinite')
    const showDeleteModal = ref(false)

    const blockDurationOptions = [
      { label: '1 día', value: '1d' },
      { label: '3 días', value: '3d' },
      { label: '7 días', value: '7d' },
      { label: '30 días', value: '30d' },
      { label: 'Indefinido', value: 'indefinite' }
    ]

    const getBlockedUntil = () => {
      if (blockDuration.value === 'indefinite') return undefined
      const days = parseInt(blockDuration.value)
      const date = new Date()
      date.setDate(date.getDate() + days)
      return date.toISOString()
    }

    const reasonLabels = {
      spam: 'Spam',
      harassment: 'Acoso',
      inappropriate_content: 'Contenido inapropiado',
      fake_profile: 'Perfil falso',
      scam: 'Estafa',
      violence: 'Violencia',
      hate_speech: 'Discurso de odio',
      other: 'Otro'
    }

    const statusLabels = {
      pending: 'Pendiente',
      reviewed: 'Revisado',
      resolved: 'Resuelto',
      dismissed: 'Descartado'
    }

    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      dismissed: 'bg-gray-100 text-gray-800'
    }

    const reasonLabel = (r) => reasonLabels[r] || r
    const statusLabel = (s) => statusLabels[s] || s
    const statusClass = (s) => statusColors[s] || 'bg-gray-100 text-gray-800'
    const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' }) : '-'

    const load = async () => {
      loading.value = true
      try {
        const res = type === 'user'
          ? await reportService.getUserReportDetail(id)
          : await reportService.getMessageReportDetail(id)
        reportData.value = res.data
      } catch {
        toast.error('Error al cargar el reporte')
      } finally {
        loading.value = false
      }
    }

    const openDecisionModal = () => {
      decisionAction.value = null
      blockReasonText.value = ''
      blockDuration.value = 'indefinite'
      showDecisionModal.value = true
    }

    const executeDecision = async () => {
      if (!decisionAction.value) return
      actionLoading.value = true
      try {
        const report = reportData.value.report
        const reason = blockReasonText.value || undefined
        const blockedUntil = getBlockedUntil()

        if (decisionAction.value === 'block_reported') {
          await reportService.blockUser(report.id, report.reportedId, 'block', reason, blockedUntil)
          toast.success('Usuario bloqueado del app')
        } else if (decisionAction.value === 'block_reporter') {
          await reportService.blockUser(report.id, report.reporterId, 'block', reason, blockedUntil)
          toast.success('Usuario bloqueado del app')
        } else if (decisionAction.value === 'block_sender') {
          await reportService.blockUser(report.id, report.message.sender.id, 'block', reason, blockedUntil)
          toast.success('Usuario bloqueado del app')
        } else if (decisionAction.value === 'delete_message') {
          await reportService.deleteReportedMessage(report.id)
          toast.success('Mensaje eliminado y reporte resuelto')
        } else if (decisionAction.value === 'dismiss') {
          if (type === 'user') {
            await reportService.updateUserReportStatus(report.id, 'dismissed')
          } else {
            await reportService.updateMessageReportStatus(report.id, 'dismissed')
          }
          toast.success('Reporte descartado')
        }

        showDecisionModal.value = false
        const res = type === 'user'
          ? await reportService.getUserReportDetail(id)
          : await reportService.getMessageReportDetail(id)
        reportData.value = res.data
      } catch {
        toast.error('Error al ejecutar la acción')
      } finally {
        actionLoading.value = false
      }
    }

    const confirmDelete = () => {
      showDeleteModal.value = true
    }

    const executeDelete = async () => {
      actionLoading.value = true
      try {
        if (type === 'user') {
          await reportService.deleteUserReport(id)
        } else {
          await reportService.deleteMessageReport(id)
        }
        toast.success('Reporte eliminado')
        showDeleteModal.value = false
        router.push('/reports')
      } catch {
        toast.error('Error al eliminar reporte')
      } finally {
        actionLoading.value = false
      }
    }

    onMounted(load)

    return {
      type, loading, actionLoading, reportData,
      showDecisionModal, decisionAction, blockReasonText, blockDuration, blockDurationOptions,
      showDeleteModal,
      reasonLabel, statusLabel, statusClass, formatDate,
      openDecisionModal, executeDecision,
      confirmDelete, executeDelete
    }
  }
}
</script>
